'use client';

import { enAU, enGB, enIE, enUS } from 'date-fns/locale';
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import { registerLocale } from 'react-datepicker';

import {
  guessDateFormatLocaleForRegion,
  setDateFormatLocale,
} from '@campaign-manager/core/lib/date';
import {
  guessNumberFormatLocaleForRegion,
  setCurrency,
  setNumberFormatLocale,
} from '@campaign-manager/core/lib/utils';
import { AvailableRegions, type RawProjectSettingsApiResponse } from '@campaign-manager/core/model';

import { useNotify } from '../Notification';

const locales = { enGB, enUS, enIE, enAU };

interface LocalizationContextValue {
  numberFormatLocale: string;
  dateFormatLocale: string;
  currency: string;
  // #FIXME: this is a hack to configure react-datepicker.
  datePickerLocale: string;
}

const LocalizationContext = createContext<LocalizationContextValue>({} as LocalizationContextValue);

interface Props {
  projectSettings: RawProjectSettingsApiResponse | null | undefined;
  children: ReactNode;
}
export function LocalizationProvider({ projectSettings, children }: Props) {
  const notify = useNotify();
  const [value, setValue] = useState<LocalizationContextValue>(() => {
    if (!projectSettings) {
      // Fallback defaults when settings not available
      return {
        numberFormatLocale: 'en-US',
        dateFormatLocale: 'en-US',
        currency: 'USD',
        datePickerLocale: 'enGB',
      };
    }
    const { currency } = projectSettings;
    const apiRegion = projectSettings.api?.region;
    const numberFormatLocale = guessNumberFormatLocaleForRegion(apiRegion);
    const dateFormatLocale = guessDateFormatLocaleForRegion(apiRegion);
    return {
      numberFormatLocale,
      dateFormatLocale,
      currency,
      datePickerLocale: 'enGB',
    };
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!projectSettings) {
      notify.error(
        'Failed to load project settings. Default localization settings will be used instead.',
      );
      console.warn("Couldn't load project settings, default localization will be used.");
      return;
    }

    const { currency } = projectSettings;
    const apiRegion = projectSettings.api?.region;

    const numberFormatLocale = guessNumberFormatLocaleForRegion(apiRegion);
    setNumberFormatLocale(numberFormatLocale);
    setCurrency(currency);

    const dateFormatLocale = guessDateFormatLocaleForRegion(apiRegion);
    setDateFormatLocale(dateFormatLocale);

    setValue({
      numberFormatLocale,
      dateFormatLocale,
      currency,
      datePickerLocale: 'enGB',
    });

    // #FIXME: this is a hack to load date-fns locale and pass it to react-datepicker.
    const importLocaleFile = async () => {
      const datePickerLocale = guessDatePickerLocale(apiRegion);
      registerLocale(datePickerLocale, locales[datePickerLocale]);

      setValue((prevValue) => ({
        ...prevValue,
        datePickerLocale,
      }));
    };
    importLocaleFile();
  }, [projectSettings]);

  return <LocalizationContext.Provider value={value}>{children}</LocalizationContext.Provider>;
}

export function useLocalization() {
  const { numberFormatLocale, dateFormatLocale, currency, datePickerLocale } =
    useContext(LocalizationContext);
  return { numberFormatLocale, dateFormatLocale, currency, datePickerLocale };
}

// #FIXME: this is a hack to configure react-datepicker.
function guessDatePickerLocale(region: AvailableRegions | undefined) {
  switch (region) {
    case AvailableRegions.EU:
      return 'enIE';
    case AvailableRegions.US_EAST:
      return 'enUS';
    case AvailableRegions.BR:
      return 'enUS';
    case AvailableRegions.AU:
      return 'enAU';
    case AvailableRegions.JP:
      return 'enGB';
    case AvailableRegions.UK:
      return 'enGB';
    default:
      return 'enGB';
  }
}
